import { useCallback, useEffect, useRef, useState } from "react";

const SAMPLE_RATE = 48000;
const CHUNK_MS = 100;
const AUDIO_THRESHOLD = 0.003;
const SILENCE_DURATION = 800;

function floatTo16BitPCM(float32Array) {
  const length = float32Array.length;
  const buffer = new ArrayBuffer(length * 2);
  const view = new DataView(buffer);

  for (let i = 0; i < length; i++) {
    const sample = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }

  return buffer;
}

class AudioPlayer {
  constructor() {
    this.audioContext = null;
    this.queue = [];
    this.nextTime = 0;
    this.isPlaying = false;
  }

  async init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
    }
  }

  async playAudio(arrayBuffer) {
    await this.init();

    const int16Array = new Int16Array(arrayBuffer);
    const float32Array = new Float32Array(int16Array.length);

    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768;
    }

    this.queue.push(float32Array);
    this.schedule();
  }

  schedule() {
    if (!this.audioContext) return;

    if (!this.isPlaying) {
      this.nextTime = Math.max(
        this.audioContext.currentTime,
        this.nextTime || 0
      );
      this.isPlaying = true;
    }

    while (this.queue.length > 0) {
      const chunk = this.queue.shift();
      const audioBuffer = this.audioContext.createBuffer(
        1,
        chunk.length,
        this.audioContext.sampleRate
      );
      audioBuffer.getChannelData(0).set(chunk);

      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);
      source.start(this.nextTime);

      this.nextTime += audioBuffer.length / this.audioContext.sampleRate;
    }

    if (this.queue.length === 0) {
      this.isPlaying = false;
    }
  }

  clear() {
    this.queue = [];
    if (this.audioContext) {
      this.nextTime = this.audioContext.currentTime;
    }
  }
}

export default function VoiceChatBot() {
  const [isActive, setIsActive] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState(null);
  const [conversationLog, setConversationLog] = useState([]);

  const wsRef = useRef(null);
  const playerRef = useRef(new AudioPlayer());
  const micRef = useRef({
    stream: null,
    audioContext: null,
    processor: null,
    source: null,
    running: false,
  });

  const addToLog = useCallback((text, speaker) => {
    setConversationLog((prev) => [
      ...prev,
      {
        id: Date.now(),
        text,
        speaker,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
  }, []);

  const startVoiceChat = async () => {
    try {
      setError(null);

      // Start backend
      console.log("🚀 Starting voice chatbot...");
      const response = await fetch(
        "https://prod.toxboxai.com/start-voice",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to start voice chat");
      }

      const ws = new WebSocket("ws://localhost:8081");
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        console.log("✅ Voice chat connected");
        setIsConnected(true);
        addToLog("Voice chat started! Speak to me.", "system");
      };

      ws.onmessage = async (event) => {
        try {
          if (typeof event.data === "string") {
            const data = JSON.parse(event.data);

            if (
              data.source === "Provaantech" &&
              data.event?.type === "transcript"
            ) {
              if (data.event.speaker === "user") {
                addToLog(data.event.text, "user");
              } else if (data.event.speaker === "agent") {
                addToLog(data.event.text, "bot");
              }
            }

            if (
              data.source === "Provaantech" &&
              data.event?.type === "playback_clear_buffer"
            ) {
              playerRef.current.clear();
              setIsListening(false);
            }
          } else {
            const arrayBuffer = event.data;
            if (arrayBuffer.byteLength > 0) {
              await playerRef.current.playAudio(arrayBuffer);
            }
          }
        } catch (error) {
          console.error("Message handling error:", error);
        }
      };

      ws.onclose = () => {
        console.log("❌ Voice chat disconnected");
        setIsConnected(false);
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setError("Connection error");
      };

      wsRef.current = ws;

      await startMicrophone();
      setIsActive(true);
    } catch (error) {
      console.error("Failed to start voice chat:", error);
      setError(error.message);
    }
  };

  const startMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: true,
        },
      });

      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      let chunkBuffers = [];
      const chunkSamplesTarget = Math.round(
        (CHUNK_MS / 1000) * audioContext.sampleRate
      );
      let lastSpeechTime = 0;
      let lastCommitTime = 0;

      processor.onaudioprocess = (event) => {
        if (!micRef.current.running) return;

        const inputData = event.inputBuffer.getChannelData(0);
        const rms = Math.sqrt(
          inputData.reduce((sum, sample) => sum + sample * sample, 0) /
          inputData.length
        );

        setAudioLevel(rms);

        const isSpeechDetected = rms > AUDIO_THRESHOLD;

        if (isSpeechDetected) {
          lastSpeechTime = Date.now();
          if (!isSpeaking) {
            setIsSpeaking(true);
            setIsListening(false);
          }
        }

        const copy = new Float32Array(inputData.length);
        copy.set(inputData);
        chunkBuffers.push(copy);

        const totalSamples = chunkBuffers.reduce(
          (sum, buffer) => sum + buffer.length,
          0
        );

        if (totalSamples >= chunkSamplesTarget) {
          const merged = new Float32Array(totalSamples);
          let offset = 0;

          for (const buffer of chunkBuffers) {
            merged.set(buffer, offset);
            offset += buffer.length;
          }

          chunkBuffers = [];
          const pcmBuffer = floatTo16BitPCM(merged);

          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(pcmBuffer);
          }

          const now = Date.now();
          const timeSinceLastSpeech = now - lastSpeechTime;

          if (
            isSpeaking &&
            timeSinceLastSpeech > SILENCE_DURATION &&
            now - lastCommitTime > 500
          ) {
            lastCommitTime = now;
            setIsSpeaking(false);
            setIsListening(true);

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: "commit" }));
            }
          }
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      micRef.current = {
        stream,
        audioContext,
        processor,
        source,
        running: true,
      };

      console.log("✅ Microphone started");
    } catch (error) {
      console.error("Microphone failed:", error);
      setError("Microphone access denied. Please allow microphone access.");
    }
  };

  const stopVoiceChat = async () => {
    try {
      if (micRef.current.running) {
        const { stream, audioContext, processor, source } = micRef.current;

        if (processor) processor.disconnect();
        if (source) source.disconnect();
        if (stream) stream.getTracks().forEach((track) => track.stop());
        if (audioContext) audioContext.close();

        micRef.current.running = false;
      }

      playerRef.current.clear();

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      await fetch(
        "https://prod.toxboxai.com/api/end-voice",
        { method: "POST" }
      );

      setIsActive(false);
      setIsConnected(false);
      setIsSpeaking(false);
      setIsListening(false);
      setAudioLevel(0);
      setError(null);

      console.log("🛑 Voice chat stopped");
    } catch (error) {
      console.error("Error stopping voice chat:", error);
    }
  };

  useEffect(() => {
    return () => {
      if (micRef.current.running) {
        stopVoiceChat();
      }
    };
  }, []);

  const getAudioLevelBar = () => {
    const percentage = Math.min((audioLevel / AUDIO_THRESHOLD) * 100, 100);
    const isActive = audioLevel > AUDIO_THRESHOLD;

    return (
      <div
        style={{
          width: 200,
          height: 20,
          backgroundColor: "#f0f0f0",
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid #ddd",
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: "100%",
            backgroundColor: isActive ? "#4CAF50" : "#FF9800",
            transition: "width 0.1s ease-out",
          }}
        />
      </div>
    );
  };

  return (
    <div
      style={{
        maxWidth: 600,
        margin: "20px auto",
        fontFamily: "Arial, sans-serif",
        border: "2px solid #ddd",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "#4CAF50",
          color: "white",
          padding: 20,
          textAlign: "center",
        }}
      >
        <h2
          style={{
            margin: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          🎙️ Voice-to-Voice AI Chatbot
          {isConnected && (
            <span
              style={{
                fontSize: "12px",
                backgroundColor: "rgba(255,255,255,0.2)",
                padding: "4px 8px",
                borderRadius: 4,
              }}
            >
              LIVE
            </span>
          )}
        </h2>
      </div>

      {/* Error Display */}
      {error && (
        <div
          style={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            padding: 15,
            borderBottom: "1px solid #ddd",
          }}
        >
          ❌ {error}
        </div>
      )}

      {!isActive ? (
        <div
          style={{
            padding: 40,
            textAlign: "center",
            backgroundColor: "#f9f9f9",
          }}
        >
          <button
            onClick={startVoiceChat}
            style={{
              padding: "15px 30px",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontSize: "18px",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            🎙️ Start Voice Chat
          </button>
          <p style={{ marginTop: 15, color: "#666", fontSize: "14px" }}>
            Click to start talking with the AI using your voice
          </p>
        </div>
      ) : (
        <>
          <div
            style={{
              padding: 20,
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #ddd",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 15,
              }}
            >
              <div>
                <strong>Status:</strong>
                <span
                  style={{
                    marginLeft: 8,
                    color: isSpeaking
                      ? "#4CAF50"
                      : isListening
                        ? "#2196F3"
                        : "#666",
                  }}
                >
                  {isSpeaking
                    ? "🗣️ You are speaking"
                    : isListening
                      ? "👂 AI is listening"
                      : "🤐 Waiting..."}
                </span>
              </div>

              <button
                onClick={stopVoiceChat}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: 5,
                  cursor: "pointer",
                }}
              >
                🛑 End Chat
              </button>
            </div>

            <div>
              <strong>Audio Level:</strong>
              <div style={{ marginTop: 8 }}>{getAudioLevelBar()}</div>
              <div style={{ fontSize: "12px", color: "#666", marginTop: 5 }}>
                Level: {audioLevel.toFixed(4)} | Threshold: {AUDIO_THRESHOLD}
              </div>
            </div>
          </div>

          {/* Conversation Log */}
          <div
            style={{
              height: 300,
              overflowY: "auto",
              padding: 20,
              backgroundColor: "#ffffff",
            }}
          >
            {conversationLog.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#666",
                  fontStyle: "italic",
                  marginTop: 50,
                }}
              >
                Start speaking to see the conversation...
              </div>
            ) : (
              conversationLog.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    marginBottom: 15,
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor:
                      entry.speaker === "user"
                        ? "#e3f2fd"
                        : entry.speaker === "bot"
                          ? "#f3e5f5"
                          : "#f5f5f5",
                    borderLeft: `4px solid ${entry.speaker === "user"
                      ? "#2196F3"
                      : entry.speaker === "bot"
                        ? "#9C27B0"
                        : "#9E9E9E"
                      }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: 5,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>
                      {entry.speaker === "user"
                        ? "👤 You"
                        : entry.speaker === "bot"
                          ? "🤖 AI"
                          : "🔧 System"}
                    </span>
                    <span>{entry.timestamp}</span>
                  </div>
                  <div style={{ fontSize: "14px", lineHeight: 1.4 }}>
                    {entry.text}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Instructions */}
          <div
            style={{
              padding: 15,
              backgroundColor: "#f0f0f0",
              fontSize: "12px",
              color: "#666",
              textAlign: "center",
            }}
          >
            💡 <strong>How to use:</strong> Simply speak naturally - the AI will
            detect when you finish talking and respond with voice
          </div>
        </>
      )}
    </div>
  );
}
